import type { OrderTargetType } from '@masters-club/types';

export interface OrderUser {
  id: number;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface OrderItemTargetInfo {
  name: string;
  price: string;
  currency: string;
}

export interface PricingTierInfo {
  id: number;
  name: string | null;
  shortDescription: string | null;
  baseCurrency: string;
  durationDays: number | null;
  gradientFrom: string | null;
  gradientTo: string | null;
}

export interface OrderItemData {
  id: number;
  targetType: OrderTargetType;
  targetId: number;
  pricingTierId: number | null;
  unitPrice: string;
  quantity: number;
  total: string;
  target: OrderItemTargetInfo | null;
  pricingTierInfo: PricingTierInfo | null;
  metadata?: {
    adults?: number;
    children?: number;
    infants?: number;
    accommodationType?: 'SINGLE' | 'DOUBLE';
    isAccommodation?: boolean;
    accommodationId?: string;
    accommodationName?: string;
  } | null;
}

export interface OrderStatusConfigInfo {
  id: number;
  name: string;
  color: string | null;
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
}

export interface Order {
  id: number;
  userId: number;
  statusConfigId: number;
  assignedAdminId: number | null;
  chatRoomId: number | null;
  promoCodeId: number | null;
  total: string;
  discountTotal: string | null;
  paidAmount: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user: OrderUser;
  statusConfig: OrderStatusConfigInfo;
  items: OrderItemData[];
}

export interface OrderStatusConfig {
  id: number;
  name: string;
  color: string;
  description: string | null;
  position: number;
  isSystem: boolean;
  isInitial: boolean;
  isFinal: boolean;
}

export interface OrderStatusTransition {
  id: number;
  fromStatusId: number | null;
  toStatusId: number;
  requireNote: boolean;
  requireConfirmation: boolean;
  toStatus?: OrderStatusConfig;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  COURSE: 'Курс',
  SUBSCRIPTION_PLAN: 'Абонемент',
  CHANNEL_PASS: 'Доступ к каналу',
  EVENT: 'Событие',
  BONUS: 'Привилегия',
};

export function getTargetTypeLabel(targetType: string): string {
  return TARGET_TYPE_LABELS[targetType] ?? targetType;
}

export function formatOrderItemSummary(item: OrderItemData): string {
  const typeLabel = getTargetTypeLabel(item.targetType);
  const meta = item.metadata as Record<string, unknown> | null;
  const name = item.targetType === 'BONUS' && meta?.['bonusName']
    ? String(meta['bonusName'])
    : (item.target?.name ?? `#${item.targetId}`);
  return `${typeLabel}: ${name}`;
}
